import { Layer } from "@carbon/react";
import { FC } from "react";

import InlineError from "src/components/Errors/InlineError.tsx";
import { Drawer } from "src/components/Modal";
import { DynamicViewRenderer } from "src/dynamic-view-renderer/DynamicViewRenderer.tsx";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { useGetCaseByIdQuery } from "src/graphql/anti-fraud/queries/cases/GetCaseByIdQuery.ts";
import { useFormatPropertyByCode } from "src/hooks/property/useFormatProperyByCode.ts";
import { useT } from "src/internationalization";

import { GRAPH_SIDE_PANEL_WIDTH_REM } from "../graph.constants";
import { GraphCaseNodeData } from "../graph.types.ts";

type GraphCaseNodePassiveModalProps = {
  onClose: () => void;
  data: GraphCaseNodeData;
};

export const GraphCaseNodePassiveModal: FC<GraphCaseNodePassiveModalProps> = ({
  data: { label, serverId },
  onClose,
}): JSX.Element => {
  const t = useT();
  const formatPropertyByCode = useFormatPropertyByCode();
  const {
    data: caseData,
    error,
    loading,
  } = useGetCaseByIdQuery({
    variables: { id: serverId },
    skip: !serverId,
  });

  const uiSchema = useDynamicViewSchema();

  const viewConfiguration = uiSchema.graph.getElementView("case");
  const request = caseData ? formatPropertyByCode(caseData.properties, "id") : "";

  return (
    <Drawer
      layer={2}
      width={`${GRAPH_SIDE_PANEL_WIDTH_REM}rem`}
      className="[&_.cds--modal-content]:overflow-y-hidden"
      onClose={onClose}
      title={label}
      closeOnOutsideClick={false}
      loading={loading}
      error={
        error
          ? {
              title: error.title,
              subtitle: error.message,
            }
          : undefined
      }
    >
      <Layer level={1} className="h-full overflow-hidden flex flex-col">
        {viewConfiguration && !error && (
          <DynamicViewRenderer
            viewConfiguration={viewConfiguration}
            data={{
              widgetsData: {
                basic: {
                  case: {
                    propertyValues: caseData?.properties,
                    elements: caseData?.elements,
                    blacklistedIdentifiers: caseData?.blacklistedIdentifiers,
                  },
                },
                executions: {
                  executionResults: caseData?.executionResults,
                  selectedCase: caseData
                    ? {
                        id: caseData.id,
                        request,
                        alerts: caseData.alerts,
                        typeCode: caseData.typeCode,
                      }
                    : undefined,
                },
              },
            }}
            isStateful={false}
          />
        )}
        {!viewConfiguration && <InlineError message={t("general.configuration.error")} />}
      </Layer>
    </Drawer>
  );
};
